import { AccountType } from "./account.js"
import { type LanguageValue } from "./account.js"
import { Address } from "./address.js"
import { BusinessRegistry } from "./business-registry.js"

export interface SignUpDto {
    /** Registry the company was validated against (cz/sk/de) or `other` for unvalidated free-text entry. */
    businessRegistry: BusinessRegistry,
    /** Registry business identifier; omitted when businessRegistry is `other`. */
    companyId?: string,
    company: {
        name: string,
        vatId?: string,
        /** Registered office address; omitted when businessRegistry is `other`. */
        address?: Address
    },
    accountType: AccountType
    fullName: string
    email: string
    password: string
    /** Must be true; user agreed to Terms of Service and Privacy Policy. */
    acceptedTerms: boolean
    /** UI language selected during signup (defaults to cs on the server). */
    language?: LanguageValue
}

/** @deprecated Use SignUpDto */
export type SignUpRequest = SignUpDto;

/** Public exhibition instant-signup: creates a pre-approved account with an artificial email. */
export interface ExhibitionSignUpDto {
    /** Czech ICO; omitted for manual (non-Czech / unknown) company entry. */
    companyId?: string;
    company: {
        name: string;
        vatId?: string;
        address?: Address;
    };
    /** Optional visitor name shown in the app; defaults to the company name. */
    visitorName?: string;
    /** UI language selected on the exhibition page (defaults to cs on the server). */
    language?: LanguageValue;
}

export interface ExhibitionSignUpResponse {
    created: boolean;
    accountType: AccountType;
    currentAccountId: string | null;
}

/** Self-service email change (claim exhibition account): sets a new email + password after verification. */
export interface ChangeEmailDto {
    newEmail: string;
    newPassword: string;
}

const EXHIBITION_EMAIL_DOMAIN = 'monomo.ai';

/** Artificial owner email for an exhibition-created account, e.g. support+12345678@monomo.ai. */
export function buildExhibitionEmail(companyId: string): string {
    return `support+${companyId.toLowerCase()}@${EXHIBITION_EMAIL_DOMAIN}`;
}

/** True for artificial support+<ico>@monomo.ai addresses created by the exhibition flow. */
export function isExhibitionArtificialEmail(email: string | null | undefined): boolean {
    if (!email) return false;
    return /^support\+[a-z0-9-]+@monomo\.ai$/i.test(email.trim());
}

export interface ForgotPasswordRequest {
    email: string;
}

export interface ResetPasswordRequest {
    email: string;
    token: string;
    password: string;
}

export interface ResendVerificationRequest {
    email: string;
}

export interface CompanySupportRequest {
    companyId: string;
    companyName: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    message: string;
}

/** Prefill data captured from LinkedIn for a brand-new sign-up awaiting account type/business selection. */
export interface LinkedinPendingSignupDto {
    email: string;
    fullName: string;
    photoUrl: string | null;
    /** Only populated once/if extended LinkedIn API access is available; null otherwise. */
    jobTitle: string | null;
    /** Free-text hint only - never used to auto-match an existing Business record. */
    employerName: string | null;
}

/** Completes a LinkedIn sign-up: the account-type/business subset of SignUpDto (name/email/password already known). */
export interface LinkedinCompleteSignupDto {
    businessRegistry: BusinessRegistry;
    companyId?: string;
    company: {
        name: string;
        vatId?: string;
        address?: Address;
    };
    accountType: AccountType;
    acceptedTerms: boolean;
}

/** Whether the current user's account has a connected LinkedIn identity (profile settings). */
export interface LinkedinConnectionStatusDto {
    connected: boolean;
    connectedAt: string | null;
}

/** Prefill data for a brand-new Google sign-up awaiting account type/business selection. */
export type GooglePendingSignupDto = LinkedinPendingSignupDto;

/** Completes a Google sign-up (account-type/business subset; identity established at OAuth). */
export type GoogleCompleteSignupDto = LinkedinCompleteSignupDto;

/** Whether the current user's account has a connected Google identity (profile settings). */
export interface GoogleConnectionStatusDto {
    connected: boolean;
    connectedAt: string | null;
}

/** Public policy for whether new self-service registrations require admin approval. */
export interface RegistrationApprovalPolicyDto {
    required: boolean;
}
